import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CaretDown, CaretUp, Check, MagnifyingGlass, X } from '@phosphor-icons/react';
import { filtereInReihenfolge, filtereOptionen, markiereTreffer, type SucheOption } from '../../../lib/immobilien/suche';
import { SegmentedControl, type Segment } from './SegmentedControl';

export type { SucheOption };

/**
 * Suchfeld mit Dropdown für große Kontaktmengen (README §9, ≈ 500 Kontakte).
 * Es wird nie die ganze Liste ausgegeben: erst ab `minZeichen` und höchstens `maxTreffer`.
 * Optional Typ-Filter (Mieter, Eigentümer, Dienstleister).
 *
 * Datenquelle entweder `optionen` (lokal gefiltert) oder `suche` (z. B. Supabase-Abfrage,
 * erhält ein AbortSignal; veraltete Antworten werden verworfen).
 *
 * Mit `knopf` als Auswahlknopf mit Popover (Objekt-Filter der Mieterliste, Handoff Mieter §5.1):
 * Liste sofort sichtbar und in ihrer Reihenfolge, `fest` steht immer oben, ↑/↓/Enter/Esc,
 * Klick außerhalb oder Auswahl schließt und leert die Suche.
 */
export interface SearchSelectProps {
  optionen?: readonly SucheOption[];
  suche?: (anfrage: string, typ: string | null, signal: AbortSignal) => Promise<SucheOption[]>;
  onWahl: (o: SucheOption) => void;
  /** Gewählte Option als Chip anzeigen (statt Suchfeld). */
  ausgewaehlt?: SucheOption | null;
  onEntfernen?: () => void;
  typen?: readonly Segment<string>[];
  label?: string;
  platzhalter?: string;
  maxTreffer?: number;
  minZeichen?: number;
  autoFocus?: boolean;
  /** Knopf-Variante: Beschriftung des Knopfs und Name für Screenreader. */
  knopf?: { inhalt: React.ReactNode; ariaLabel: string; icon?: React.ReactNode };
  /** Knopf-Variante: Einträge, die immer oben stehen (z. B. „Alle Objekte“). */
  fest?: readonly SucheOption[];
  /** Knopf-Variante: gewählter Eintrag (Haken, Hervorhebung). */
  gewaehltId?: string | null;
  /** Knopf-Variante: eigene Zeile; ohne Angabe Label + Untertitel. */
  zeile?: (o: SucheOption, z: { aktiv: boolean; gewaehlt: boolean }) => React.ReactNode;
  /** Knopf-Variante: Text ohne Treffer. */
  leerText?: string;
  /** Knopf-Variante: Zeile unter der Suche, z. B. „12 von 70 Objekten“ (erhält die Trefferzahl). */
  info?: (treffer: number) => string;
}

const ALLE = '__alle';

export function SearchSelect(props: SearchSelectProps) {
  return props.knopf ? <SucheKnopf {...props} knopf={props.knopf} /> : <SucheFeld {...props} />;
}

function SucheFeld({
  optionen, suche, onWahl, ausgewaehlt, onEntfernen, typen, label, platzhalter = 'Name suchen …', maxTreffer = 8, minZeichen = 1, autoFocus,
}: SearchSelectProps) {
  const [anfrage, setAnfrage] = useState('');
  const [typ, setTyp] = useState<string>(ALLE);
  const [treffer, setTreffer] = useState<SucheOption[]>([]);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState(false);
  const [offen, setOffen] = useState(false);
  const [aktiv, setAktiv] = useState(0);
  const listId = useId();
  const feldId = useId();
  const huelle = useRef<HTMLDivElement>(null);

  const typWert = typ === ALLE ? null : typ;
  const genug = anfrage.trim().length >= minZeichen;

  useEffect(() => {
    if (!genug) { setTreffer([]); setLaedt(false); return; }
    if (optionen) {
      setTreffer(filtereOptionen(optionen, anfrage, typWert, maxTreffer));
      return;
    }
    if (!suche) return;
    const ctrl = new AbortController();
    setLaedt(true);
    setFehler(false);
    const t = setTimeout(() => {
      suche(anfrage, typWert, ctrl.signal)
        .then(r => { if (!ctrl.signal.aborted) setTreffer(r.slice(0, maxTreffer)); })
        .catch(() => { if (!ctrl.signal.aborted) { setTreffer([]); setFehler(true); } })
        .finally(() => { if (!ctrl.signal.aborted) setLaedt(false); });
    }, 180);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [anfrage, typWert, optionen, suche, maxTreffer, genug]);

  useEffect(() => { setAktiv(0); }, [treffer]);

  // Klick außerhalb schließt das Dropdown
  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => { if (huelle.current && !huelle.current.contains(e.target as Node)) setOffen(false); };
    document.addEventListener('mousedown', zu);
    return () => document.removeEventListener('mousedown', zu);
  }, [offen]);

  const waehle = (o: SucheOption) => {
    onWahl(o);
    setAnfrage('');
    setOffen(false);
  };

  const tastatur = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOffen(true); setAktiv(a => Math.min(a + 1, treffer.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAktiv(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { const o = treffer[aktiv]; if (offen && o) { e.preventDefault(); waehle(o); } }
    else if (e.key === 'Escape') { if (offen) { e.stopPropagation(); setOffen(false); } }
  };

  if (ausgewaehlt) {
    return (
      <div className="flex flex-col" style={{ gap: 6 }}>
        {label && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{label}</span>}
        <div className="flex items-center" style={{ minHeight: 50, borderRadius: 15, gap: 10, padding: '6px 8px 6px 14px', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="truncate" style={{ fontSize: 15, fontWeight: 700 }}>{ausgewaehlt.label}</span>
            {ausgewaehlt.sub && <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{ausgewaehlt.sub}</span>}
          </div>
          {onEntfernen && (
            // 44 px Klickfläche um den 30-px-Kreis (Hit-Target mobil), ohne die Zeile höher zu machen
            <button type="button" aria-label="Auswahl entfernen" onClick={onEntfernen} className="flex items-center justify-center cursor-pointer shrink-0"
              style={{ width: 44, height: 44, margin: '-7px -7px -7px 0', border: 'none', background: 'transparent', color: 'var(--ink)', padding: 0 }}>
              <span className="flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--fill2)' }}>
                <X weight="bold" size={12} />
              </span>
            </button>
          )}
        </div>
      </div>
    );
  }

  const zeigeListe = offen && genug;
  const aktiveId = treffer[aktiv] ? `${listId}-${treffer[aktiv].id}` : undefined;

  return (
    <div ref={huelle} className="relative flex flex-col" style={{ gap: 8 }}>
      {label && <label htmlFor={feldId} style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{label}</label>}
      {typen && typen.length > 0 && (
        <SegmentedControl
          segmente={[{ key: ALLE, label: 'Alle' }, ...typen]}
          wert={typ}
          onChange={setTyp}
          hoehe={30}
          ariaLabel="Kontakttyp"
        />
      )}
      <div className="flex items-center" style={{ height: 50, borderRadius: 15, gap: 10, padding: '0 14px', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
        <MagnifyingGlass size={17} color="var(--ink3)" aria-hidden />
        <input
          id={feldId}
          role="combobox"
          aria-expanded={zeigeListe}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={zeigeListe ? aktiveId : undefined}
          autoComplete="off"
          autoFocus={autoFocus}
          value={anfrage}
          placeholder={platzhalter}
          onChange={e => { setAnfrage(e.target.value); setOffen(true); }}
          onFocus={() => setOffen(true)}
          onKeyDown={tastatur}
          className="flex-1 min-w-0 h-full"
          style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}
        />
      </div>
      {zeigeListe && (
        <ul
          id={listId}
          role="listbox"
          className="immo-glas-stark immo-scroll absolute left-0 right-0 m-0 list-none overflow-auto"
          style={{ top: '100%', marginTop: 6, zIndex: 30, maxHeight: 360, borderRadius: 18, padding: 6 }}
        >
          {laedt && treffer.length === 0 && <li style={{ padding: '10px 12px', fontSize: 13, color: 'var(--ink3)' }}>Suche läuft …</li>}
          {fehler && <li style={{ padding: '10px 12px', fontSize: 13, color: 'var(--bad)' }}>Suche fehlgeschlagen – bitte erneut versuchen.</li>}
          {!laedt && !fehler && treffer.length === 0 && <li style={{ padding: '10px 12px', fontSize: 13, color: 'var(--ink3)' }}>Keine Treffer.</li>}
          {treffer.map((o, i) => (
            <li
              key={o.id}
              id={`${listId}-${o.id}`}
              role="option"
              aria-selected={i === aktiv}
              onMouseDown={e => e.preventDefault()}
              onClick={() => waehle(o)}
              onMouseEnter={() => setAktiv(i)}
              className="flex flex-col cursor-pointer"
              style={{ padding: '8px 12px', borderRadius: 12, background: i === aktiv ? 'var(--acc-soft)' : 'transparent' }}
            >
              <span className="truncate" style={{ fontSize: 14 }}>
                {markiereTreffer(o.label, anfrage).map((t, k) => (
                  <span key={k} style={{ fontWeight: t.treffer ? 800 : 600 }}>{t.text}</span>
                ))}
              </span>
              {o.sub && <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{o.sub}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SucheKnopf({
  knopf, optionen = [], fest = [], gewaehltId, onWahl, zeile, platzhalter = 'Suchen …', leerText = 'Keine Treffer.', info,
}: SearchSelectProps & { knopf: NonNullable<SearchSelectProps['knopf']> }) {
  const [offen, setOffen] = useState(false);
  const [anfrage, setAnfrage] = useState('');
  const [aktiv, setAktiv] = useState(0);
  const huelle = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const knopfRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  // #557: Liste schwebt über allem (Portal, feste Position am Knopf) — in Dialogen mit eigener
  // Scrollfläche wurde sie sonst abgeschnitten. Unten zu wenig Platz → nach oben aufklappen.
  const [lage, setLage] = useState<{ left: number; width: number; top?: number; bottom?: number; listeMax: number } | null>(null);
  const platziere = () => {
    const k = huelle.current?.getBoundingClientRect();
    if (!k) return;
    const vw = window.innerWidth, vh = window.innerHeight;
    const width = Math.min(Math.max(k.width, 340), vw - 32);
    const left = Math.max(16, Math.min(k.left + width > vw - 16 ? k.right - width : k.left, vw - width - 16));
    const unten = vh - k.bottom - 16, oben = k.top - 16;
    const nachOben = unten < 300 && oben > unten;
    const platz = (nachOben ? oben : unten) - 110;
    setLage(nachOben ? { left, width, bottom: vh - k.top + 6, listeMax: Math.max(140, Math.min(320, platz)) }
      : { left, width, top: k.bottom + 6, listeMax: Math.max(140, Math.min(320, platz)) });
  };
  useLayoutEffect(() => {
    if (!offen) { setLage(null); return; }
    platziere();
    const neu = (e: Event) => { if (e.target instanceof Node && panelRef.current?.contains(e.target)) return; platziere(); };
    window.addEventListener('scroll', neu, true);
    window.addEventListener('resize', platziere);
    return () => { window.removeEventListener('scroll', neu, true); window.removeEventListener('resize', platziere); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);

  const treffer = filtereInReihenfolge(optionen, anfrage);
  const eintraege = [...fest, ...treffer];

  const schliessen = (fokus: boolean) => {
    setOffen(false);
    setAnfrage('');
    if (fokus) knopfRef.current?.focus();
  };

  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => {
      const t = e.target as Node;
      if (huelle.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOffen(false); setAnfrage('');
    };
    document.addEventListener('mousedown', zu);
    return () => document.removeEventListener('mousedown', zu);
  }, [offen]);

  useEffect(() => { setAktiv(0); }, [anfrage]);

  const waehle = (o: SucheOption) => { onWahl(o); schliessen(true); };

  const tastatur = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAktiv(a => Math.min(a + 1, eintraege.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAktiv(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { const o = eintraege[aktiv]; if (o) { e.preventDefault(); waehle(o); } }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); schliessen(true); }
    else if (e.key === 'Tab') { schliessen(false); }
  };

  const aktiveId = eintraege[aktiv] ? `${listId}-${eintraege[aktiv].id}` : undefined;

  return (
    <div ref={huelle} className="relative" style={{ flex: '0 1 280px', minWidth: 200 }}>
      <button
        ref={knopfRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={offen}
        aria-label={knopf.ariaLabel}
        onClick={() => (offen ? schliessen(false) : setOffen(true))}
        className="immo-glas flex items-center w-full cursor-pointer"
        style={{ height: 44, padding: '0 12px 0 14px', borderRadius: 22, border: 'none', gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--ink)', boxShadow: offen ? 'inset 0 0 0 2px var(--acc)' : undefined }}
      >
        {knopf.icon}
        {knopf.inhalt}
        {offen ? <CaretUp weight="bold" size={12} color="var(--ink3)" /> : <CaretDown weight="bold" size={12} color="var(--ink3)" />}
      </button>
      {offen && lage && createPortal(
        <div
          ref={panelRef}
          data-ueber-modal
          className="immo-side-in fixed flex flex-col"
          style={{
            left: lage.left, width: lage.width, ...(lage.top !== undefined ? { top: lage.top } : { bottom: lage.bottom }),
            zIndex: 470, borderRadius: 22, padding: 8, gap: 6, color: 'var(--ink)', fontFamily: "'Manrope', -apple-system, sans-serif",
            background: 'var(--bg)', boxShadow: '0 0 0 1px var(--line), 0 24px 50px -16px var(--shade)',
          }}
        >
          <div className="flex items-center" style={{ height: 40, borderRadius: 14, gap: 8, padding: '0 12px', background: 'var(--fill2)' }}>
            <MagnifyingGlass size={15} color="var(--ink3)" aria-hidden />
            <input
              autoFocus
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={aktiveId}
              aria-label={platzhalter}
              autoComplete="off"
              value={anfrage}
              placeholder={platzhalter}
              onChange={e => setAnfrage(e.target.value)}
              onKeyDown={tastatur}
              className="flex-1 min-w-0"
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--ink)' }}
            />
          </div>
          {info && <span className="immo-zahl" aria-live="polite" style={{ padding: '0 6px', fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>{info(treffer.length)}</span>}
          <ul id={listId} role="listbox" className="immo-scroll m-0 p-0 list-none flex flex-col overflow-auto" style={{ maxHeight: lage.listeMax, gap: 1 }}>
            {eintraege.map((o, i) => {
              const gewaehlt = o.id === gewaehltId;
              const istAktiv = i === aktiv;
              return (
                <li
                  key={o.id}
                  id={`${listId}-${o.id}`}
                  role="option"
                  aria-selected={gewaehlt}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => waehle(o)}
                  onMouseEnter={() => setAktiv(i)}
                  className="flex items-center cursor-pointer"
                  style={{ gap: 10, padding: '8px 10px', borderRadius: 12, background: gewaehlt ? 'var(--acc-soft)' : istAktiv ? 'var(--hover)' : 'transparent' }}
                >
                  {zeile ? zeile(o, { aktiv: istAktiv, gewaehlt }) : (
                    <span className="flex flex-col flex-1 min-w-0">
                      <span className="truncate" style={{ fontSize: 13, fontWeight: gewaehlt ? 800 : 600 }}>{o.label}</span>
                      {o.sub && <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{o.sub}</span>}
                    </span>
                  )}
                  <Check weight="bold" size={13} color="var(--acc)" style={{ flexShrink: 0, visibility: gewaehlt ? 'visible' : 'hidden' }} aria-hidden />
                </li>
              );
            })}
            {treffer.length === 0 && (
              <li style={{ padding: 16, textAlign: 'center', fontSize: 13, color: 'var(--ink3)' }}>{leerText}</li>
            )}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
